import React, { useState, useRef, useEffect, useMemo } from 'react';
import { MapPin } from 'lucide-react';
import { Input } from './ui/input';
import { suburbs } from '../data/suburbs';

export const SuburbAutocomplete = ({ id, value, onChange, placeholder, testId }) => {
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const wrapRef = useRef(null);

  const matches = useMemo(() => {
    const q = value.trim().toLowerCase();
    if (q.length < 2) return [];
    const starts = [];
    const contains = [];
    for (const s of suburbs) {
      const l = s.toLowerCase();
      if (l.startsWith(q)) starts.push(s);
      else if (l.includes(q)) contains.push(s);
    }
    return [...starts, ...contains].slice(0, 8);
  }, [value]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const select = (suburb) => {
    onChange(suburb);
    setOpen(false);
    setHighlighted(-1);
  };

  const onKeyDown = (e) => {
    if (!open || matches.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => (h + 1) % matches.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => (h <= 0 ? matches.length - 1 : h - 1));
    } else if (e.key === 'Enter' && highlighted >= 0) {
      e.preventDefault();
      select(matches[highlighted]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <Input
        id={id}
        data-testid={testId}
        placeholder={placeholder}
        value={value}
        autoComplete="off"
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setHighlighted(-1);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        className="py-6 text-base"
      />
      {open && matches.length > 0 && (
        <ul
          data-testid={`${testId}-suggestions`}
          className="absolute z-30 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-xl"
        >
          {matches.map((s, i) => (
            <li key={s}>
              <button
                type="button"
                data-testid={`${testId}-option-${i}`}
                onMouseDown={(e) => { e.preventDefault(); select(s); }}
                onMouseEnter={() => setHighlighted(i)}
                className={`w-full flex items-center gap-2 px-4 py-3 text-left text-base text-gray-800 transition-colors ${
                  highlighted === i ? 'bg-[#FF8C42]/10 text-[#1E3A5F]' : ''
                }`}
              >
                <MapPin className="w-4 h-4 text-[#FF8C42] shrink-0" />
                {s}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
